/**
 * MeetingsHubPage — Wave 4C
 *
 * Unified Calendar + Meetings page at /roros/rhythm/calendar.
 * Two tabs: Calendar (grid, default) and List (table).
 *
 * AC-4C-1: Tab state reflected in URL (?view=calendar|list).
 * AC-4C-2: Filters hoisted and shared across tabs.
 * AC-4C-3: Selected meeting persists across tab switches.
 * AC-4C-4: All actions (detail modal, edit, run, skip/cancel) available from both tabs.
 * AC-4C-5: Single "Calendar" nav entry (layout.config.ts updated separately).
 * AC-4C-6: Multi-tenancy/RBAC preserved (org-scoped hooks, permission gates).
 *
 * Single Scroll Container Rule: no overflow-y-auto on child elements.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Plus, CalendarX, MessagesSquare, Calendar as CalendarIcon } from 'lucide-react';
import { RorosPage } from '@/components/roros/RorosPage';
import { Calendar } from '@/components/ui/Calendar/Calendar';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Table, type TableColumn } from '@/components/ui/Table';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryPage } from '@/components/QueryPage';
import { t } from '@/lib/i18n';
import { useRorosMeetingsCalendar, useMeetingsList } from '@/hooks/roros/meetings';
import { usePermissions } from '@/hooks/usePermissions';
import { useFormSheetUrlRouting } from '@/hooks/useFormSheetUrlRouting';
import { PERMISSIONS } from '@/constants/permissions';
import type { CalendarEvent, CalendarViewMode } from '@/components/ui/Calendar/types';
import type {
  CalendarMeetingDTO,
  RorosMeetingDTO,
  MeetingType,
  MeetingStatus,
} from '@/types/roros/meetings';
import { RecurringRibbon } from '@/components/roros/meetings/RecurringRibbon';
import { MeetingChip } from '@/components/roros/meetings/MeetingChip';
import { MeetingEditModal } from '@/components/roros/meetings/MeetingEditModal';
import { MeetingDetailReadOnlyModal } from '@/components/roros/meetings/MeetingDetailReadOnlyModal';
import { formatDateTimeStr } from '@/lib/localeFormatters';

// ─── Constants ────────────────────────────────────────────────────────────────

const TYPE_VARIANT: Record<MeetingType, CalendarEvent['colorVariant']> = {
  daily:     'gray',
  weekly:    'primary',
  monthly:   'secondary',
  quarterly: 'warning',
  trimester: 'warning',
  annual:    'error',
  ad_hoc:    'gray',
};

const STATUS_FILTERS = [
  { value: 'all',         label: () => t('All') },
  { value: 'scheduled',   label: () => t('Scheduled') },
  { value: 'in_progress', label: () => t('In Progress') },
  { value: 'completed',   label: () => t('Completed') },
] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function meetingToCalendarEvent(m: CalendarMeetingDTO): CalendarEvent & { _meeting: CalendarMeetingDTO } {
  const start = new Date(m.scheduledAt);
  const durationMs = (m.durationMinutes ?? 30) * 60 * 1000;
  const end = new Date(start.getTime() + durationMs);

  const statusMap: Record<string, CalendarEvent['status']> = {
    scheduled:   'scheduled',
    in_progress: 'scheduled',
    completed:   'sent',
    skipped:     'draft',
    cancelled:   'canceled',
  };

  return {
    id: m.id ?? `virtual-${m.cadenceScheduleId}-${m.scheduledAt}`,
    title: m.title,
    start,
    end,
    colorVariant: TYPE_VARIANT[m.meetingType as MeetingType] ?? 'gray',
    status: statusMap[m.status] ?? 'scheduled',
    _meeting: m,
  };
}

function meetingStatusVariant(status: MeetingStatus): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'completed':   return 'success';
    case 'in_progress': return 'warning';
    case 'paused':      return 'warning';
    case 'cancelled':   return 'error';
    case 'skipped':     return 'default';
    default:            return 'default';
  }
}

function meetingStatusLabel(status: MeetingStatus): string {
  switch (status) {
    case 'scheduled':   return t('Scheduled');
    case 'in_progress': return t('In Progress');
    case 'paused':      return t('Paused');
    case 'completed':   return t('Completed');
    case 'skipped':     return t('Skipped');
    case 'cancelled':   return t('Cancelled');
    default:            return status;
  }
}

function meetingTypeLabel(type: MeetingType): string {
  switch (type) {
    case 'daily':     return t('Daily');
    case 'weekly':    return t('Weekly');
    case 'monthly':   return t('Monthly');
    case 'quarterly': return t('Quarterly');
    case 'trimester': return t('Trimester');
    case 'annual':    return t('Annual');
    case 'ad_hoc':    return t('Ad Hoc');
    default:          return type;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MeetingsHubPage() {
  const navigate = useNavigate();
  // AC-MUT2-6: capture origin for returnTo state passed to MeetingWizardPage
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('roros.meetings.write');
  const canRead  = hasPermission(PERMISSIONS.ROROS_MEETINGS_READ);

  // ─── AC-4C-1: Tab state in URL (?view=calendar|list) ─────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'list' ? 'list' : 'calendar';

  const setView = useCallback((v: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', v);
      return next;
    });
  }, [setSearchParams]);

  // ─── AC-4C-2: Shared filters ──────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>(() => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: toDateStr(from), to: toDateStr(to) };
  });

  // ─── AC-4C-3: Shared selection / detail modal ─────────────────────────────
  const [selectedMeeting, setSelectedMeeting] = useState<CalendarMeetingDTO | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // ─── Edit modal state ─────────────────────────────────────────────────────
  const [editingMeetingId, setEditingMeetingId] = useState<string | null>(null);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);

  // ─── Ad-hoc create modal state ────────────────────────────────────────────
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createInitialAt, setCreateInitialAt] = useState<string | undefined>(undefined);

  // ─── Data hooks ───────────────────────────────────────────────────────────
  const { data: calendarMeetings, isLoading: calLoading, isError: calError, refetch: calRefetch } =
    useRorosMeetingsCalendar(dateRange.from, dateRange.to);

  const listQueryParams = useMemo(
    () => statusFilter !== 'all' ? { status: statusFilter } : undefined,
    [statusFilter],
  );
  const listQuery = useMeetingsList(listQueryParams);

  // ─── URL deep-link routing for edit sheet ────────────────────────────────
  const getMeetingId = useCallback((m: CalendarMeetingDTO) => m.id ?? '', []);
  const meetingsWithId = (calendarMeetings ?? []).filter((m) => !!m.id);

  const { deepLinkTarget, consumeDeepLink } = useFormSheetUrlRouting({
    urlKey: 'edit-meeting',
    items: meetingsWithId,
    getId: getMeetingId,
  });

  useEffect(() => {
    if (!deepLinkTarget || !deepLinkTarget.id) return;
    setEditingMeetingId(deepLinkTarget.id);
    setIsEditSheetOpen(true);
    consumeDeepLink();
  }, [deepLinkTarget, consumeDeepLink]);

  // ─── Shared action handlers ───────────────────────────────────────────────

  const handleOpenDetail = useCallback((meeting: CalendarMeetingDTO) => {
    setSelectedMeeting(meeting);
    setIsDetailModalOpen(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setIsDetailModalOpen(false);
    // Keep selectedMeeting set so AC-4C-3 selection persists across tab switches
  }, []);

  const handleEdit = useCallback((meetingId: string) => {
    setEditingMeetingId(meetingId);
    setIsEditSheetOpen(true);
  }, []);

  const handleRun = useCallback((meetingId: string) => {
    // AC-MUT2-6: pass current page as returnTo so wizard finish link returns here
    navigate(`/roros/meetings/${meetingId}/run`, {
      state: { returnTo: location.pathname + location.search },
    });
  }, [navigate, location]);

  const handleCalendarRefresh = useCallback(() => { calRefetch(); }, [calRefetch]);

  // ─── Calendar tab handlers ────────────────────────────────────────────────

  const handleRangeChange = useCallback((from: Date, to: Date, _view: CalendarViewMode) => {
    setDateRange({ from: toDateStr(from), to: toDateStr(to) });
  }, []);

  const handleEventClick = useCallback((event: CalendarEvent) => {
    const ext = event as ReturnType<typeof meetingToCalendarEvent>;
    if (ext._meeting) handleOpenDetail(ext._meeting);
  }, [handleOpenDetail]);

  const handleDayClick = useCallback((date: Date) => {
    if (!canWrite) return;
    const midday = new Date(date);
    midday.setHours(12, 0, 0, 0);
    setCreateInitialAt(midday.toISOString());
    setIsCreateModalOpen(true);
  }, [canWrite]);

  const handleAdHocClick = useCallback(() => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 1);
    setCreateInitialAt(now.toISOString());
    setIsCreateModalOpen(true);
  }, []);

  // ─── Calendar events ──────────────────────────────────────────────────────

  const calendarEvents: CalendarEvent[] = useMemo(() => {
    return (calendarMeetings ?? [])
      .filter((m) => m.meetingType !== 'daily')
      .filter((m) => statusFilter === 'all' || m.status === statusFilter)
      .map(meetingToCalendarEvent);
  }, [calendarMeetings, statusFilter]);

  // ─── List table columns ───────────────────────────────────────────────────

  const listColumns: TableColumn<RorosMeetingDTO>[] = useMemo(() => [
    {
      key: 'title',
      title: t('Title'),
      render: (_val, row) => (
        <Button
          type="button"
          variant="ghost"
          className="h-auto p-0 font-medium text-foreground hover:text-primary-600 hover:underline justify-start"
          data-testid={`meeting-row-title-${row.id}`}
          onClick={() => handleOpenDetail(row as unknown as CalendarMeetingDTO)}
        >
          {row.title}
        </Button>
      ),
    },
    {
      key: 'meetingType',
      title: t('Type'),
      render: (_val, row) => (
        <span className="text-muted-foreground text-sm">{meetingTypeLabel(row.meetingType)}</span>
      ),
    },
    {
      key: 'status',
      title: t('Status'),
      render: (_val, row) => (
        <Badge variant={meetingStatusVariant(row.status)}>
          {meetingStatusLabel(row.status)}
        </Badge>
      ),
    },
    {
      key: 'scheduledAt',
      title: t('Scheduled At'),
      render: (_val, row) => (
        <span className="text-muted-foreground text-sm">
          {row.scheduledAt
            ? formatDateTimeStr(row.scheduledAt)
            : '—'}
        </span>
      ),
    },
    {
      key: 'id',
      title: t('Actions'),
      render: (_val, row) => (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleOpenDetail(row as unknown as CalendarMeetingDTO)}
            data-testid={`meeting-action-detail-${row.id}`}
            aria-label={t('View meeting details')}
          >
            {t('View')}
          </Button>
          {canWrite && row.id && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleEdit(row.id!)}
              data-testid={`meeting-action-edit-${row.id}`}
              aria-label={t('Edit meeting')}
            >
              {t('Edit')}
            </Button>
          )}
          {row.id && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleRun(row.id!)}
              data-testid={`meeting-action-run-${row.id}`}
              aria-label={t('Run meeting')}
            >
              {t('Run')}
            </Button>
          )}
        </div>
      ),
    },
  ], [canWrite, handleOpenDetail, handleEdit, handleRun]);

  // ─── List query adapter ───────────────────────────────────────────────────

  const listQueryAdapted = useMemo(() => ({
    data: listQuery.data,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    error: listQuery.error as Error | null,
    refetch: () => { void listQuery.refetch(); },
  }), [listQuery]);

  const listEmptyAction = canRead ? (
    <Button
      variant="outline"
      onClick={() => setView('calendar')}
      data-testid="meetings-go-to-calendar"
    >
      {t('Go to Calendar')}
    </Button>
  ) : undefined;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <RorosPage
      title={t('Calendar')}
      icon={<CalendarIcon className="h-5 w-5" />}
      actions={
        canWrite ? (
          <Button
            size="sm"
            onClick={handleAdHocClick}
            aria-label={t('+ Ad-hoc meeting')}
            data-testid="adhoc-meeting-cta"
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('+ Ad-hoc meeting')}
          </Button>
        ) : null
      }
    >
      {/* ── AC-4C-2: Shared status filter pills ── */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="meetings-status-filters">
        <span className="text-sm text-muted-foreground mr-1">{t('Status:')}</span>
        {STATUS_FILTERS.map((opt) => (
          <Button
            key={opt.value}
            variant={statusFilter === opt.value ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(opt.value)}
            data-testid={`filter-status-${opt.value}`}
          >
            {opt.label()}
          </Button>
        ))}
      </div>

      {/* ── AC-4C-1: Tab bar ── */}
      <Tabs value={view} onValueChange={setView} className="mb-4">
        <TabsList aria-label={t('Calendar view selector')}>
          <TabsTrigger value="calendar" data-testid="meetings-tab-calendar">
            {t('Calendar')}
          </TabsTrigger>
          <TabsTrigger value="list" data-testid="meetings-tab-list">
            {t('List')}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* ── Calendar tab content ── */}
      {view === 'calendar' && (
        <>
          <RecurringRibbon meetings={calendarMeetings ?? []} />

          {calError && !calLoading && (
            <EmptyState
              icon={<CalendarX className="h-10 w-10 text-destructive" />}
              title={t('Failed to load meetings')}
              description={t('An error occurred while loading your meetings.')}
              action={{ label: t('Retry'), onClick: () => calRefetch() }}
            />
          )}

          {!calError && (
            <div className="relative">
              {calLoading && (
                <div className="absolute inset-0 z-10 flex items-start justify-center pt-20 pointer-events-none">
                  <LoadingState />
                </div>
              )}
              <Calendar
                events={calendarEvents}
                onEventClick={handleEventClick}
                onRangeChange={handleRangeChange}
                onDayClick={canWrite ? handleDayClick : undefined}
                eventRenderer={(event) => {
                  const ext = event as ReturnType<typeof meetingToCalendarEvent>;
                  return ext._meeting ? (
                    <MeetingChip
                      meeting={ext._meeting}
                      onUpdate={handleCalendarRefresh}
                    />
                  ) : null;
                }}
              />
              {!calLoading && calendarEvents.length === 0 && (
                <div className="mt-4">
                  <EmptyState
                    icon={<CalendarIcon className="h-10 w-10 text-muted-foreground" />}
                    title={t('No meetings this period')}
                    description={t('Upcoming meetings will appear here once the cadence evaluator runs.')}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── List tab content ── */}
      {view === 'list' && (
        <QueryPage
          query={listQueryAdapted}
          emptyMessage={t('No meetings yet')}
          emptyIcon={<MessagesSquare className="h-8 w-8 text-muted-foreground" />}
          emptyAction={listEmptyAction}
        >
          {(items) => (
            <div data-testid="meetings-table">
              <Table
                data={items as RorosMeetingDTO[]}
                columns={listColumns}
                searchable={false}
                pagination={false}
              />
            </div>
          )}
        </QueryPage>
      )}

      {/* ── Shared modals (AC-4C-3: selection persists across tab switches) ── */}

      {/* Detail modal (read-only overview) */}
      {selectedMeeting && isDetailModalOpen && (
        <MeetingDetailReadOnlyModal
          meeting={selectedMeeting}
          isOpen={isDetailModalOpen}
          onClose={handleCloseDetail}
          onEdit={selectedMeeting.id && canWrite ? () => {
            handleCloseDetail();
            handleEdit(selectedMeeting.id!);
          } : undefined}
        />
      )}

      {/* Edit sheet */}
      {isEditSheetOpen && editingMeetingId && (
        <MeetingEditModal
          mode="edit"
          meetingId={editingMeetingId}
          isOpen={isEditSheetOpen}
          onClose={() => {
            setIsEditSheetOpen(false);
            setEditingMeetingId(null);
          }}
          onSaved={() => {
            setIsEditSheetOpen(false);
            setEditingMeetingId(null);
            void calRefetch();
            void listQuery.refetch();
          }}
          urlKey="edit-meeting"
        />
      )}

      {/* Ad-hoc create modal */}
      {isCreateModalOpen && (
        <MeetingEditModal
          mode="create"
          initialScheduledAt={createInitialAt}
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSaved={() => {
            setIsCreateModalOpen(false);
            void calRefetch();
            void listQuery.refetch();
          }}
        />
      )}
    </RorosPage>
  );
}
